// src/pages/Menu/Articulacion/Details/AddEntityModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  X,
  Globe as GlobeIcon,
  MapPin,
  Landmark,
  Briefcase,
  Image as ImageIcon,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  createRegionalEntity,
  updateRegionalEntity,
} from "../../../../services/regionalEntitiesService";
import TooltipHelper from "../../../../components/common/TooltipHelper";

/* ---------- helpers ---------- */
const normalizeUrl = (s) => {
  const t = String(s || "").trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
};

const resolveImageUrl = (ent) => {
  const raw =
    ent?.imageUrl ||
    ent?.image_url ||
    ent?.logoUrl ||
    ent?.logo_url ||
    ent?.logo ||
    "";
  if (!raw) return "";
  const base = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
  if (/^https?:\/\//i.test(raw)) return raw;
  return base ? `${base}${raw.startsWith("/") ? "" : "/"}${raw}` : raw;
};

/* ---------- validación ---------- */
const schema = Yup.object({
  scope: Yup.mixed().oneOf(["national", "regional"]).required(),
  name: Yup.string().required("El nombre es obligatorio").max(255, "Máx 255 caracteres"),
  location: Yup.string().nullable().max(255, "Máx 255 caracteres"),
  website: Yup.string().url("URL inválida").nullable().transform((v) => (v === "" ? null : v)),
  description: Yup.string().nullable(),
  sector: Yup.mixed()
    .oneOf(["public", "private"], "Selecciona público o privado")
    .required("Selecciona público o privado"),
  image: Yup.mixed().nullable(),
});

/* ---------- UI helpers ---------- */
const input =
  "w-full border rounded-xl px-3 py-2.5 border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none";
const labelSm = "block text-sm font-medium text-gray-700 mb-1";
const Required = () => <span className="text-red-600 ml-0.5" aria-hidden="true">*</span>;

/* Segmented / Toggle bonito */
const Segmented = ({ value, onChange, options, ariaLabel, required }) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    aria-required={required || undefined}
    className="inline-flex flex-wrap gap-1 rounded-2xl border border-gray-200 bg-white p-1"
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(opt.value)}
          className={[
            "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition",
            active
              ? "bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white shadow"
              : "text-gray-700 hover:bg-gray-50",
          ].join(" ")}
          title={opt.label}
        >
          {opt.icon}
          <span>{opt.label}</span>
        </button>
      );
    })}
  </div>
);

/* =============================================================== */

export default function AddEntityModal({
  open,
  onClose,
  entity,      // si viene => edición
  onSaved,     // callback con entidad creada/actualizada
}) {
  const isEdit = Boolean(entity?.id);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!open) setPreview("");
  }, [open]);

  const initial = useMemo(
    () => ({
      scope: entity?.scope === "national" ? "national" : "regional",
      name: entity?.name || "",
      location: entity?.region || entity?.location || "",
      website: entity?.website || "",
      description: entity?.description || "",
      sector: entity?.sector || "", // requerido (Yup obliga a elegir)
      image: null,
    }),
    [entity]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      {/* Modal */}
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        <Formik
          initialValues={initial}
          validationSchema={schema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload = {
                scope: values.scope,
                name: values.name?.trim(),
                region: values.location?.trim() || null,
                website: values.website ? normalizeUrl(values.website) : null,
                description: values.description?.trim() || null,
                sector: values.sector, // requerido
              };

              let result;
              if (values.image instanceof File) {
                const fd = new FormData();
                Object.entries(payload).forEach(([k, v]) => v != null && fd.append(k, v));
                fd.append("image", values.image);
                result = isEdit
                  ? await updateRegionalEntity(entity.id, fd)
                  : await createRegionalEntity(fd);
              } else {
                result = isEdit
                  ? await updateRegionalEntity(entity.id, payload)
                  : await createRegionalEntity(payload);
              }

              const data = result?.data ?? result ?? null;

              await Swal.fire({
                icon: "success",
                title: isEdit ? "Actualizada" : "Creada",
                timer: 1400,
                showConfirmButton: false,
              });

              onSaved?.(data);
              onClose?.();
            } catch (err) {
              const msg =
                err?.response?.data?.message ||
                err?.message ||
                "No se pudo guardar";
              await Swal.fire({ icon: "error", title: "Error", text: msg });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <>
              {/* Header tipo banner */}
              <div
                className="px-5 py-4 text-white"
                style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">
                    {isEdit ? "Editar entidad" : "Añadir entidad"}
                  </div>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label="Cerrar">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Cuerpo scrollable del formulario */}
              <Form className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                {/* Chip ámbito en vivo */}
                <div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full border ${
                      values.scope === "national"
                        ? "bg-blue-50 border-blue-100 text-[#0c3b66]"
                        : "bg-emerald-50 border-emerald-100 text-emerald-700"
                    }`}
                  >
                    Ámbito: {values.scope === "national" ? "Nacional" : "Regional"}
                  </span>
                </div>

                {/* Ámbito (obligatorio) */}
                <div>
                  <div className={labelSm}>
                    Ámbito <Required />
                  </div>
                  <Segmented
                    ariaLabel="Ámbito de la entidad"
                    required
                    value={values.scope}
                    onChange={(v) => setFieldValue("scope", v)}
                    options={[
                      { value: "regional", label: "Regional", icon: <MapPin size={16} /> },
                      { value: "national", label: "Nacional", icon: <GlobeIcon size={16} /> },
                    ]}
                  />
                  <ErrorMessage name="scope" component="div" className="text-sm text-red-600 mt-1" />
                </div>

                {/* Nombre (obligatorio) */}
                <div>
                  <label className={labelSm} htmlFor="name">
                    Nombre de la entidad <Required />
                  </label>
                  <Field
                    id="name"
                    name="name"
                    className={input}
                    placeholder="Ej: CORFO Coquimbo / Ministerio de Economía"
                    maxLength={255}
                    aria-required="true"
                  />
                  <ErrorMessage name="name" component="div" className="text-sm text-red-600 mt-1" />
                </div>

                {/* Ubicación (opcional) */}
                <div>
                  <label className={`${labelSm} flex items-center gap-1`} htmlFor="location">
                    Ubicación
                    <TooltipHelper text={values.scope === "national" ? "Para entidades nacionales, indica país o ciudad principal." : "Para entidades regionales, indica la región o ciudad donde opera."} />
                  </label>
                  <Field
                    id="location"
                    name="location"
                    className={input}
                    placeholder={
                      values.scope === "national"
                        ? "País / Ciudad (texto libre). Ej: Chile"
                        : "Región / Ciudad (texto libre). Ej: Coquimbo"
                    }
                    maxLength={255}
                  />
                  <ErrorMessage name="location" component="div" className="text-sm text-red-600 mt-1" />
                </div>

                {/* Sitio web (opcional) */}
                <div>
                  <label className={`${labelSm} flex items-center gap-1`} htmlFor="website">
                    Sitio web
                    <TooltipHelper text="URL completa del sitio web. Si no incluyes http:// o https://, se agregará automáticamente." />
                  </label>
                  <Field id="website" name="website" className={input} placeholder="https://…" />
                  <ErrorMessage name="website" component="div" className="text-sm text-red-600 mt-1" />
                </div>

                {/* Descripción (opcional) */}
                <div>
                  <label className={labelSm} htmlFor="description">Descripción</label>
                  <Field
                    as="textarea"
                    rows={3}
                    id="description"
                    name="description"
                    className={input}
                    placeholder="Resumen o notas…"
                  />
                </div>

                {/* Tipo de entidad (obligatorio) */}
                <div>
                  <div className={labelSm}>
                    Tipo de entidad <Required />
                  </div>
                  <Segmented
                    ariaLabel="Tipo de entidad"
                    required
                    value={values.sector}
                    onChange={(v) => setFieldValue("sector", v)}
                    options={[
                      { value: "public", label: "Público", icon: <Landmark size={16} /> },
                      { value: "private", label: "Privado", icon: <Briefcase size={16} /> },
                    ]}
                  />
                  <ErrorMessage name="sector" component="div" className="text-sm text-red-600 mt-1" />
                </div>

                {/* Logo / Imagen (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    Logo / Imagen
                    <TooltipHelper text="Formatos: JPG, PNG. Se recomienda una imagen cuadrada o con fondo transparente para mejor visualización." />
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 cursor-pointer w-fit">
                    <ImageIcon size={16} />
                    <span>{(values.image && values.image.name) || "Seleccionar archivo"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.currentTarget.files?.[0] || null;
                        setFieldValue("image", file);
                        setPreview(file ? URL.createObjectURL(file) : "");
                      }}
                    />
                  </label>
                  {(preview || resolveImageUrl(entity)) && (
                    <img
                      src={preview || resolveImageUrl(entity)}
                      alt="preview"
                      className="h-14 w-14 mt-2 rounded-lg object-contain bg-white ring-1 ring-gray-200 p-1"
                      onError={(ev) => (ev.currentTarget.style.display = "none")}
                    />
                  )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 left-0 right-0 bg-white pt-2 mt-2 border-t flex justify-end gap-2">
                  <button 
                    type="button" 
                    onClick={onClose} 
                    className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
                  >
                    {isEdit ? (isSubmitting ? "Actualizando…" : "Actualizar") : isSubmitting ? "Guardando…" : "Guardar"}
                  </button>
                </div>
              </Form>
            </>
          )}
        </Formik>
      </div>
    </div>
  );
}
